<template>
	<VerticalLayout size="middle">
		<Skeleton v-if="isVrs" :paragraph="{ rows: 1 }" :title="false" :loading="loading">
			<HorizontalLayout class="pd-vertical" size="middle" :direction="buttonsDirection">
				<LabeledSwitch
					data-testid="settings-vrs-fallback-near-switch"
					v-model="fallbackNearEnabled"
					:rightLabel="$t('settings.vrsSettingsForm.vrsFallbackNearEnabled')"
					:disabled="disabled || loading"
				/>
				<Select
					data-testid="settings-vrs-fallback-near-select"
					class="ui-select"
					v-model:value="fallbackNearId"
					:options="fallbackNearOptions"
					:disabled="disabled || loading"
				/>
			</HorizontalLayout>
		</Skeleton>
		<Skeleton v-if="!isVrs" :paragraph="{ rows: 3 }" :title="false" :loading="loading">
			<VerticalLayout class="near-modes-container pd-vertical" size="middle">
				<FormItem
					class="near-ephemeris-source-form-item"
					:label="$t('settings.vrsSettingsForm.nearEphemerisSourceLabel')"
					:colon="false"
				>
					<RadioGroup
						data-testid="settings-near-on-brdcm-switch"
						v-model:value="nearOnBrdcmEnabled"
						:options="nearEphemerisSourceOptions"
						option-type="button"
						:disabled="disabled || loading || isNearOnBrdcmSubmitting || !onUpdateField"
						@change="onNearOnBrdcmChange"
					/>
				</FormItem>
				<LabeledSwitch
					data-testid="settings-brdcm-external-brdc-switch"
					v-model="brdcmExternalBrdcEnabled"
					:rightLabel="$t('settings.vrsSettingsForm.brdcmExternalBrdcEnabledLabel')"
					:loading="isBrdcmExternalBrdcSubmitting"
					:disabled="disabled || loading || isBrdcmExternalBrdcSubmitting || !onUpdateField"
					@change="onBrdcmExternalBrdcChange"
				/>
				<div class="near-fallback-row">
					<LabeledSwitch
						data-testid="settings-near-sp3-brdcm-fallback-switch"
						v-model="nearSp3BrdcmFallback"
						:rightLabel="$t('settings.vrsSettingsForm.nearSp3BrdcmFallbackLabel')"
						:loading="isNearSp3BrdcmFallbackSubmitting"
						:disabled="disabled || loading || isNearSp3BrdcmFallbackSubmitting || !onUpdateField"
						@change="onNearSp3BrdcmFallbackChange"
					/>
					<Tooltip placement="topLeft" color="black">
						<template #title>{{ $t('settings.vrsSettingsForm.nearSp3BrdcmFallbackHint') }}</template>
						<QuestionCircleOutlined class="near-fallback-help-icon" />
					</Tooltip>
				</div>
			</VerticalLayout>
		</Skeleton>
		<Skeleton :paragraph="{ rows: 1 }" :title="false" :loading="loading">
			<HorizontalLayout v-if="filterOptions.length > 0" size="middle" :direction="buttonsDirection">
				<TypographyText v-if="status.maxRtcmfMountpointsCount === 1">
					<Space v-if="filterOptions.length > 0">
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.fullMountpointLabel') }}
						<TypographyText strong>{{ filterOptions[0].label }}</TypographyText>
						<Button class="pd-lg-left" :disabled="disabled" @click="onRenameRtcmfMountpointClick">
							{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.renameButtonLabel') }}
						</Button>
					</Space>
					<span v-else>  </span>
				</TypographyText>
				<Select v-else
					class="ui-select"
					v-model:value="filterSelectedId"
					:options="filterOptions"
					:disabled="disabled"
					option-label-prop="label"
				/>
				<HorizontalLayout v-if="status.maxRtcmfMountpointsCount !== 1">
					<Button
						class="width-100"
						type="primary"
						:disabled="disabled || (status.maxRtcmfMountpointsCount > 0 && filterOptions.length >= status.maxRtcmfMountpointsCount)"
						@click="onAddRtcmfMountpointClick"
					>
						<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.addButtonLabel') }}
					</Button>
					<Button class="width-100" :disabled="disabled" @click="onRenameRtcmfMountpointClick">
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.renameButtonLabel') }}
					</Button>
					<PopconfirmButton
						:title="$t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteConfirmation')"
						:ok-text="$t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteButtonLabel')"
						:cancel-text="$t('settings.vrsSettingsForm.rtcmMessagesFilters.cancelButtonLabel')"
						:disabled="disabled || filterOptions.length <= 1"
						:onConfirm="onDeleteRtcmfMountpointConfirm"
						placement="topRight"
						buttonClass="width-100"
					>
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteButtonLabel') }}
					</PopconfirmButton>
				</HorizontalLayout>
			</HorizontalLayout>
			<VerticalLayout v-else>
				<TypographyParagraph>
					{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.description') }}
				</TypographyParagraph>
				<Button type="primary" :disabled="disabled" @click="onAddRtcmfMountpointClick">
					<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.addButtonLabel') }}
				</Button>
			</VerticalLayout>
		</Skeleton>
		<RtcmMessagesFiltersForm
			v-if="filterOptions.length > 0"
			:loading="loading"
			:status="status"
			:settings="settings"
			:rtcmFilters="rtcmFilters"
			:isVrs="isVrs"
			:fetchSettings="fetchSettings"
			:disabled="disabled"
			:fallbackEnabled="fallbackNearEnabled"
			:fallbackNearId="fallbackNearId"
		/>
	</VerticalLayout>

	<RtcmMessagesFiltersAddRenameModal
		v-model="addRenamePopupVisible"
		:addMode="addMode"
		:mountpointId="filterSelectedId"
		:mountpointLabel="selectedMountpointLabel"
		:baseMountpoint="baseMountpoint"
		:networkId="networkId"
		:onSuccess="onRtcmfMountpointDone"
		:fetchSettings="fetchSettings"
	/>
</template>

<script>
import { ref, computed, toRef, watch, nextTick } from 'vue';
import { Space, Select, Button, Skeleton, TypographyParagraph, TypographyText, Tooltip, FormItem, RadioGroup, message } from 'ant-design-vue';
import { PlusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import { useSmallScreen } from '@/utils/useSmallScreen';
import { useProvideInjectFormChangedStatuses } from '@/utils/useProvideInject';

import { useI18n } from 'vue-i18n';

import { showErrorMessage } from '@/components/common/errorMessage';

import Api from '@/api/Api';
import Validators from '@/api/Validators';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';

import RtcmMessagesFiltersForm from '@/components/settings/rtcmFilters/RtcmMessagesFiltersForm.vue';
import RtcmMessagesFiltersAddRenameModal from '@/components/settings/rtcmFilters/RtcmMessagesFiltersAddRenameModal.vue';

export default {
	components: {
		Space, Select, Button, PlusCircleOutlined, QuestionCircleOutlined, Skeleton, TypographyParagraph, TypographyText, Tooltip, FormItem, RadioGroup,
		RtcmMessagesFiltersForm,
		LabeledSwitch,
		VerticalLayout, PopconfirmButton, HorizontalLayout,
		RtcmMessagesFiltersAddRenameModal,
	},
	props: {
		loading: {},
		status: {},
		settings: {},
		fetchSettings: {},
		isVrs: {},
		disabled: {
			type: Boolean,
			default: false,
		},
		onUpdateField: {
			type: Function,
			default: null,
		},
		nearMountpoints: {
			type: Array,
			default: () => [],
		},
		nameNear: {
			type: String,
			default: '',
		},
	},
	setup(props) {
		const { t } = useI18n();
		const settingsRef = toRef(props, 'settings');
		const isVrsRef = toRef(props, 'isVrs');
		const filterSelectedId = ref(null);
		const baseMountpoint = computed(() => {
			if (settingsRef.value == null) {
				return '';
			}
			return isVrsRef.value ? settingsRef.value.finalNameVrs : settingsRef.value.nameNear;
		});
		const networkId = computed(() => {
			if (settingsRef.value == null) {
				return 0;
			}
			return isVrsRef.value ? settingsRef.value.id : 0;
		});
		const filterOptions = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				return settingsRef.value.rtcmfMountpoints.map(item => ({
					value: item.id,
					label: baseMountpoint.value + '_' + item.label,
				}));
			}
			return [];
		});
		watch(filterOptions, items => {
			if (items.length > 0 && !items.some(item => item.value === filterSelectedId.value)) {
				filterSelectedId.value = items[0].value;
			}
		});
		const { smallScreen } = useSmallScreen(780);
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');

		const addRenamePopupVisible = ref(false);
		const addMode = ref(false);
		const onAddRtcmfMountpointClick = () => {
			addRenamePopupVisible.value = true;
			addMode.value = true;
		};
		const onRenameRtcmfMountpointClick = () => {
			addRenamePopupVisible.value = true;
			addMode.value = false;
		};
		const onRtcmfMountpointDone = id => {
			filterSelectedId.value = id;
			props.fetchSettings();
		};
		const onDeleteRtcmfMountpointConfirm = () => {
			Api.rtcmfMountpointDelete(filterSelectedId.value, isVrsRef.value, response => {
				if (response.status === 'ok') {
					filterSelectedId.value = null;
					message.success(t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteSuccess'));
					props.fetchSettings();
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		const rtcmFilters = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				const rtcmFilters = settingsRef.value.rtcmfMountpoints.find(item => item.id === filterSelectedId.value);
				if (rtcmFilters != null) {
					return rtcmFilters;
				}
			}
			return {
				id: 0,
				rtcm_messages_filters: [],
			};
		});

		const nearOnBrdcmEnabled = ref(false);
		const nearEphemerisSourceOptions = [
			{ label: 'SP3', value: false },
			{ label: 'BRDCM', value: true },
		];
		const brdcmExternalBrdcEnabled = ref(false);
		const nearSp3BrdcmFallback = ref(false);
		const fallbackNearEnabled = ref(false);
		const fallbackNearId = ref('');
		const { injectValue } = useProvideInjectFormChangedStatuses;
		const formChangedStatuses = injectValue({});
		const { pauseWatch, resetChangedState } = Validators.useWatchFormChanges(
			[fallbackNearEnabled, fallbackNearId],
			formChangedStatuses,
			'VrsFallbackNear',
		);
		const nearMountpointsRef = toRef(props, 'nearMountpoints');
		const nameNearRef = toRef(props, 'nameNear');
		const fallbackNearOptions = computed(() => {
			const mountpointOptions = (nearMountpointsRef.value || []).map(item => ({
				value: item.id,
				label: nameNearRef.value + '_' + item.label,
			}));
			return [
				{ value: '', label: t('settings.vrsSettingsForm.vrsFallbackNearNothingSelected') },
				...mountpointOptions,
			];
		});
		watch(settingsRef, value => {
			nearOnBrdcmEnabled.value = Boolean(value?.nearOnBrdcmEnabled);
			brdcmExternalBrdcEnabled.value = Boolean(value?.brdcmExternalBrdcEnabled);
			nearSp3BrdcmFallback.value = Boolean(value?.nearSp3BrdcmFallback);
			pauseWatch();
			fallbackNearEnabled.value = Boolean(value?.vrsFallbackNearEnabled);
			fallbackNearId.value = value?.vrsFallbackNear == null ? '' : value.vrsFallbackNear;
			nextTick(resetChangedState);
		}, { immediate: true });

		const postImmediateField = (field, value, submitting) => {
			if (!props.onUpdateField) {
				return;
			}
			submitting.value = true;
			props.onUpdateField(field, value, response => {
				submitting.value = false;
				if (response.status !== 'ok') {
					showErrorMessage(response.errors);
					props.fetchSettings();
					return;
				}
				message.success(t('settings.vrsSettingsForm.messages.success'));
			});
		};

		const isNearOnBrdcmSubmitting = ref(false);
		const onNearOnBrdcmChange = event => {
			postImmediateField('nearOnBrdcmEnabled', event.target.value, isNearOnBrdcmSubmitting);
		};

		const isBrdcmExternalBrdcSubmitting = ref(false);
		const onBrdcmExternalBrdcChange = value => {
			postImmediateField('brdcmExternalBrdcEnabled', value, isBrdcmExternalBrdcSubmitting);
		};

		const isNearSp3BrdcmFallbackSubmitting = ref(false);
		const onNearSp3BrdcmFallbackChange = value => {
			postImmediateField('nearSp3BrdcmFallback', value, isNearSp3BrdcmFallbackSubmitting);
		};

		const selectedMountpointLabel = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				const item = settingsRef.value.rtcmfMountpoints.find(item => item.id === filterSelectedId.value);
				if (item != null) {
					return item.label;
				}
			}
			return '';
		});
		return {
			filterSelectedId,
			filterOptions,
			selectedMountpointLabel,
			rtcmFilters,
			buttonsDirection,

			addRenamePopupVisible,
			addMode,
			baseMountpoint,
			networkId,
			onAddRtcmfMountpointClick,
			onRenameRtcmfMountpointClick,
			onRtcmfMountpointDone,
			onDeleteRtcmfMountpointConfirm,
			nearOnBrdcmEnabled,
			nearEphemerisSourceOptions,
			isNearOnBrdcmSubmitting,
			onNearOnBrdcmChange,
			brdcmExternalBrdcEnabled,
			isBrdcmExternalBrdcSubmitting,
			onBrdcmExternalBrdcChange,
			nearSp3BrdcmFallback,
			isNearSp3BrdcmFallbackSubmitting,
			onNearSp3BrdcmFallbackChange,
			fallbackNearEnabled,
			fallbackNearId,
			fallbackNearOptions,
		}
	},
}
</script>

<style scoped>
.ui-select {
	width: clamp(100px, 30vw, 400px);
}
.near-modes-container {
	width: 100%;
}
.near-ephemeris-source-form-item {
	margin-bottom: 0;
}
.near-ephemeris-source-form-item :deep(.ant-form-item-label) {
	padding-bottom: 0;
}
.near-fallback-row {
	display: flex;
	align-items: center;
	gap: 8px;
}
.near-fallback-help-icon {
	cursor: help;
}
@media (max-width: 900px) {
	.ui-select {
		width: 100%;
	}
}
</style>
